import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, OnInit, Provider } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap } from 'rxjs/operators';

import { CommunicationWidget, CreateAsyncServiceRequestSchema } from '@pwp-common';

import { CommunicationWidgetCreateAsyncServiceRequestService } from '../../../../services/communication/communication-widget/communication-widget-create-async-service-request/communication-widget-create-async-service-request.service';
import { FormGroupControlValueAccessor } from '../../../generic/abstract-classes/form-group-control-value-accessor';
import { ServiceCommandEditorOutput } from '../editor-output/service-command-editor-output';

const VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ServiceCommandEditorCreateAsyncServiceRequestComponent),
  multi: true,
};

const VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => ServiceCommandEditorCreateAsyncServiceRequestComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-service-command-editor-create-async-service-request',
  templateUrl: './service-command-editor-create-async-service-request.component.html',
  styleUrls: ['./service-command-editor-create-async-service-request.component.scss'],
  providers: [VALUE_ACCESSOR, VALIDATOR],
})
export class ServiceCommandEditorCreateAsyncServiceRequestComponent
  extends FormGroupControlValueAccessor<ServiceCommandEditorOutput, ServiceCommandEditorOutput>
  implements OnInit
{
  /////////////////////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////////////////////

  public selectedCommunicationWidget: CommunicationWidget | null = null;

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    private communicationWidgetService: CommunicationWidgetCreateAsyncServiceRequestService,
  ) {
    super(changeDetectorRef);
  }

  public ngOnInit(): void {
    this.createAsyncServiceRequestWidgetId.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((widgetId) => widgetId != null),
        switchMap((widgetId) => {
          this.selectedCommunicationWidget = null;
          return this.communicationWidgetService.getDoc(widgetId);
        }),
      )
      .subscribe((widget) => {
        this.selectedCommunicationWidget = widget;
        this.changeDetectorRef.markForCheck();
      });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Define Form
  /////////////////////////////////////////////////////////////////////////////////////////////

  public defineForm() {
    const formConfig = {};
    formConfig[CreateAsyncServiceRequestSchema.commandName] = new UntypedFormControl(
      CreateAsyncServiceRequestSchema.Defaults.commandName,
      [Validators.required],
    );
    formConfig[CreateAsyncServiceRequestSchema.createAsyncServiceRequestWidgetId] = new UntypedFormControl(undefined, [
      Validators.required,
    ]);
    this.form = new UntypedFormGroup(formConfig);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Parse Value Change
  /////////////////////////////////////////////////////////////////////////////////////////////
  public parseValueChange(value: any): ServiceCommandEditorOutput {
    return value;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////
  public get createAsyncServiceRequestWidgetId() {
    return this.form.get(CreateAsyncServiceRequestSchema.createAsyncServiceRequestWidgetId) as UntypedFormControl;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////
  protected makeValidationErrors(): ValidationErrors {
    return {
      'service-command-editor-create-async-service-request': this.form.value,
    };
  }
}
